import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeviceCard from './DeviceCard';
import {
  HiOutlineOfficeBuilding,
  HiOutlineClock,
  HiOutlineEye,
  HiOutlineStatusOnline,
  HiOutlineStatusOffline,
  HiOutlineRefresh,
  HiOutlineLocationMarker
} from 'react-icons/hi';
import { TbBuildingFactory2, TbSunrise, TbDoorEnter, TbDevices } from 'react-icons/tb';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { MdOutlineCheckCircle } from 'react-icons/md';

const API_URL = '/api';

function Dashboard({ token, user, onZoneClick }) {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [zones, setZones] = useState([]);
  const [devices, setDevices] = useState([]);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFacilities();
  }, []);

  useEffect(() => {
    if (selectedFacility) {
      loadZones();
      loadDevices();
      
      // ✅ HER 3 SANİYEDE BİR CİHAZLARI YÜKLE
      const interval = setInterval(() => {
        loadDevices();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [selectedFacility]);

  const loadFacilities = async () => {
    try {
      const response = await axios.get(`${API_URL}/facilities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFacilities(response.data);
      if (response.data.length > 0) {
        setSelectedFacility(response.data[0]._id);
      }
    } catch (err) {
      console.error('Tesisler yüklenemedi:', err);
    }
  };

  const loadZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/zones?facility=${selectedFacility}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(response.data);
    } catch (err) {
      console.error('Bölümler yüklenemedi:', err);
    }
  };

  const loadDevices = async () => {
    if (loading) setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filtered = response.data.filter(d => {
        const deviceFacilityId = d.facility?._id?.toString() || d.facility?.toString() || d.facility;
        return deviceFacilityId === selectedFacility;
      });
      
      setDevices(filtered);
    } catch (err) {
      console.error('Cihazlar yüklenemedi:', err);
    } finally {
      if (loading) setLoading(false);
    }
  };

  const controlDevice = async (deviceId, relayNumber, action) => {
    try {
      await axios.post(
        `${API_URL}/devices/${deviceId}/control`,
        { relayNumber, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTimeout(loadDevices, 500);
    } catch (err) {
      alert('Komut gönderilemedi: ' + err.response?.data?.error);
    }
  };

  const resetDevice = async (deviceId, deviceName) => {
    if (!window.confirm(`${deviceName} cihazını yeniden başlatmak istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await axios.post(
        `${API_URL}/devices/${deviceId}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`${deviceName} yeniden başlatılıyor. Cihaz yaklaşık 10-15 saniye içinde tekrar online olacak.`);
      setTimeout(loadDevices, 2000);
    } catch (err) {
      alert('Reset komutu gönderilemedi: ' + (err.response?.data?.error || err.message));
    }
  };

  if (facilities.length === 0) {
    return (
      <div className="card">
        <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <TbBuildingFactory2 size={24} /> Tesis Bulunamadı
        </h3>
        <div className="no-devices">
          Henüz tesis eklenmemiş. Admin ile iletişime geçin.
        </div>
      </div>
    );
  }

  const totalDevices = devices.length;
  const onlineDevices = devices.filter(d => d.isOnline && d.isPaired).length;
  const offlineDevices = totalDevices - onlineDevices;

  return (
    <div className="dashboard-grid">
      <div className="card full-width">
        <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <TbBuildingFactory2 size={22} /> Tesis Seçimi
        </h3>
        <div className="facility-selector">
          {facilities.map(facility => (
            <button
              key={facility._id}
              className={`facility-btn ${selectedFacility === facility._id ? 'active' : ''}`}
              onClick={() => setSelectedFacility(facility._id)}
            >
              <div className="facility-name">{facility.facilityName}</div>
              <div className="facility-info" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                <HiOutlineLocationMarker size={14} /> {facility.location?.address || 'Konum yok'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedFacility && !loading && (
        <div className="card full-width">
          <div 
            className="device-stats-card"
            onClick={() => setShowDeviceModal(true)}
            style={{
              background: 'linear-gradient(135deg, #2D2D2D 0%, #1D1D1D 100%)',
              padding: '25px',
              borderRadius: '15px',
              cursor: 'pointer',
              transition: 'all 0.3s',
              border: '2px solid #3D3D3D'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#E63946';
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(230, 57, 70, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#3D3D3D';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px'}}>
              <div>
                <h3 style={{margin: 0, fontSize: '18px', color: '#ccc', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <TbDevices size={22} /> TANIMLI CİHAZLAR
                </h3>
                <div style={{display: 'flex', alignItems: 'baseline', gap: '10px', marginTop: '10px'}}>
                  <span style={{fontSize: '48px', fontWeight: '700', color: '#fff'}}>{totalDevices}</span>
                  <span style={{fontSize: '24px', color: '#27ae60', fontWeight: '600'}}>{onlineDevices}</span>
                  <span style={{fontSize: '16px', color: '#999'}}>Online</span>
                </div>
              </div>
              <div style={{textAlign: 'right'}}>
                {offlineDevices > 0 && (
                  <div style={{
                    background: 'rgba(230, 57, 70, 0.2)',
                    border: '2px solid #E63946',
                    borderRadius: '10px',
                    padding: '15px 25px'
                  }}>
                    <div style={{fontSize: '32px', fontWeight: '700', color: '#E63946'}}>
                      {offlineDevices}
                    </div>
                    <div style={{fontSize: '14px', color: '#E63946', fontWeight: '600', textTransform: 'uppercase'}}>
                      OFFLİNE
                    </div>
                  </div>
                )}
                {offlineDevices === 0 && totalDevices > 0 && (
                  <div><MdOutlineCheckCircle size={48} color="#27ae60" /></div>
                )}
              </div>
            </div>
            <div style={{marginTop: '15px', fontSize: '13px', color: '#999', textAlign: 'center'}}>
              Detayları görmek için tıklayın
            </div>
          </div>
        </div>
      )}

      {zones.map(zone => {
        const zoneTypeConfig = {
          'hol_ic': { icon: HiOutlineOfficeBuilding, label: 'HOL İÇ', color: '#3B82F6' },
          'hol_dis': { icon: TbDoorEnter, label: 'HOL DIŞ', color: '#10B981' },
          'cevre_aydinlatma': { icon: HiOutlineLightBulb, label: 'ÇEVRE AYDINLATMA', color: '#F59E0B' }
        };
        const ZoneIcon = zoneTypeConfig[zone.zoneType]?.icon || HiOutlineOfficeBuilding;
        const zoneColor = zoneTypeConfig[zone.zoneType]?.color || '#666';

        return (
          <div key={zone._id} className="card zone-card">
            <div className="zone-header">
              <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <ZoneIcon size={20} color={zoneColor} />
                {zoneTypeConfig[zone.zoneType]?.label} - {zone.zoneName}
              </h3>
              <button
                className="detail-btn"
                onClick={() => onZoneClick(zone._id)}
                title="Detayları görmek için tıklayın"
                style={{display: 'flex', alignItems: 'center', gap: '4px'}}
              >
                <HiOutlineEye size={16} /> Detay
              </button>
            </div>

            {zone.automation?.enabled && (
              <div className="automation-info" style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                <HiOutlineClock size={16} />
                Otomasyon: {zone.automation.scheduleMode === 'sunrise_sunset' ? 'Gün Doğumu/Batımı' : zone.automation.scheduleMode === 'time' ? `${zone.automation.timeSchedule?.length || 0} Program` : 'Manuel'}
              </div>
            )}
          </div>
        );
      })}

      {showDeviceModal && (
        <div className="modal-overlay" onClick={() => setShowDeviceModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{maxWidth: '900px'}}>
            <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <TbDevices size={22} /> {facilities.find(f => f._id === selectedFacility)?.facilityName} - Tüm Cihazlar ({totalDevices})
            </h3>
            <div style={{marginBottom: '20px', display: 'flex', gap: '15px'}}>
              <div style={{
                flex: 1,
                background: 'rgba(39, 174, 96, 0.1)',
                border: '2px solid #27ae60',
                borderRadius: '10px',
                padding: '15px',
                textAlign: 'center'
              }}>
                <div style={{fontSize: '32px', fontWeight: '700', color: '#27ae60'}}>{onlineDevices}</div>
                <div style={{fontSize: '14px', color: '#27ae60', fontWeight: '600'}}>ONLİNE</div>
              </div>
              <div style={{
                flex: 1,
                background: 'rgba(230, 57, 70, 0.1)',
                border: '2px solid #E63946',
                borderRadius: '10px',
                padding: '15px',
                textAlign: 'center'
              }}>
                <div style={{fontSize: '32px', fontWeight: '700', color: '#E63946'}}>{offlineDevices}</div>
                <div style={{fontSize: '14px', color: '#E63946', fontWeight: '600'}}>OFFLİNE</div>
              </div>
            </div>

            <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
              {devices.length === 0 ? (
                <div className="no-devices">Bu tesiste henüz cihaz eklenmemiş</div>
              ) : (
                <div style={{display: 'grid', gap: '15px'}}>
                  {devices.map(device => {
                    const zone = zones.find(z => z._id === (device.zone?._id || device.zone));
                    const isOnline = device.isOnline && device.isPaired;
                    
                    return (
                      <div
                        key={device._id}
                        style={{
                          background: isOnline ? 'rgba(39, 174, 96, 0.1)' : 'rgba(230, 57, 70, 0.1)',
                          border: `2px solid ${isOnline ? '#27ae60' : '#E63946'}`,
                          borderRadius: '12px',
                          padding: '20px',
                          transition: 'all 0.3s'
                        }}
                      >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px'}}>
                          <div style={{flex: 1}}>
                            <div style={{fontSize: '18px', fontWeight: '700', color: '#fff', marginBottom: '5px'}}>
                              {device.deviceName}
                            </div>
                            <div style={{fontSize: '13px', color: '#999', display: 'flex', alignItems: 'center', gap: '4px'}}>
                              <HiOutlineLocationMarker size={14} /> {zone?.zoneName || 'Bölüm atanmamış'}
                            </div>
                          </div>
                          
                          <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                            <div style={{
                              background: isOnline ? '#27ae60' : '#E63946',
                              color: 'white',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              {isOnline ? <><HiOutlineStatusOnline size={14} /> ONLİNE</> : <><HiOutlineStatusOffline size={14} /> OFFLİNE</>}
                            </div>

                            {user?.role === 'admin' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  resetDevice(device._id, device.deviceName);
                                }}
                                style={{
                                  background: '#FF6B6B',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#FF5252';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#FF6B6B';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                                title="Cihazı yeniden başlat"
                              >
                                <HiOutlineRefresh size={14} /> RESET
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
                          Son Görülme: {device.lastSeen ? new Date(device.lastSeen).toLocaleString('tr-TR') : 'Hiç görülmedi'}
                        </div>
                        
                        <div style={{marginTop: '10px', fontSize: '12px', color: '#999'}}>
                          {device.relays?.length || 0} Röle • {device.hasDHT ? '🌡️ DHT22' : ''} {device.hasOLED ? '📺 OLED' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowDeviceModal(false)}
                className="btn-secondary"
              >
                KAPAT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
